'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m } from 'motion/react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Alert01IconSolid, Cancel01Icon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
interface StreamedImageSlotProps {
  imageUrl: string;
  /**
   * Inline preview data-URI (AI generate flow). When set, the slot DISPLAYS
   * these bytes (zero network, never the gateway) and falls back to `imageUrl`
   * only when absent (manual uploads, or a reload after the session-only
   * preview is gone). The durable `imageUrl` (IPFS) is for submission.
   */
  imagePreview?: string;
  /**
   * AI generate flow: the durable pin permanently failed. The preview still
   * displays, but the slot has no submittable URL — surface a small badge.
   */
  pinFailed?: boolean;
  /** Localised tooltip for the pin-failed badge. */
  pinFailedLabel?: string;
  isStreaming: boolean;
  isFlagged?: boolean;
  onClickOpen: () => void;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Optional motion config. Both defaults match `ai-edit-form.tsx`. */
  motionInitial?: false | {
    opacity: number;
    y: number;
  };
  motionTransition?: {
    duration: number;
  } | typeof MOTION_ENTRANCE;
  /** Localised alt text + button label keys. */
  altLabel: string;
  removeLabel: string;
  /** Size class for the square slot — defaults to 128px (`w-32 min-h-32`). */
  sizeClass?: string;
  /** Optimized-image `sizes` attribute. Defaults to "128px". */
  sizesAttr?: string;
}

/**
 * Square AI-streamed image slot — three states:
 *   1. Populated  — clickable thumbnail + ✕ remove overlay.
 *   2. Streaming  — pulsing skeleton placeholder.
 *   3. Empty + idle — dashed-border upload affordance, click to pick.
 *
 * Shared by:
 *   - `ai/ai-edit-form.tsx` (cover image)
 *   - `suggested-outcomes/wizard/SuggestOutcomeWizard.tsx` (single-outcome image)
 *
 * Originally inline as `CoverImageSlot` in ai-edit-form; lifted +
 * generalised so the Suggested-outcomes wizard reuses the same UX (#34
 * audit). The ✕ button position matches the proposal-creation pattern
 * (top-right, semi-transparent background).
 */
export function StreamedImageSlot(t0) {
  const $ = _c(29);
  const {
    imageUrl,
    imagePreview,
    pinFailed: t1,
    pinFailedLabel,
    isStreaming,
    isFlagged: t2,
    onClickOpen,
    onRemove,
    motionInitial: t3,
    motionTransition: t4,
    altLabel,
    removeLabel,
    sizeClass: t5,
    sizesAttr: t6
  } = t0;
  const pinFailed = t1 === undefined ? false : t1;
  const isFlagged = t2 === undefined ? false : t2;
  let t7;
  if ($[0] !== t3) {
    t7 = t3 === undefined ? {
      opacity: 0,
      y: 4
    } : t3;
    $[0] = t3;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const motionInitial = t7;
  const motionTransition = t4 === undefined ? MOTION_ENTRANCE : t4;
  const sizeClass = t5 === undefined ? "w-32 min-h-32" : t5;
  const sizesAttr = t6 === undefined ? "128px" : t6;
  const displaySrc = imagePreview !== undefined && imagePreview.length > 0 ? imagePreview : imageUrl;
  if (displaySrc.length > 0) {
    const t8 = `relative shrink-0 ${sizeClass}`;
    let t9;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        opacity: 1,
        y: 0
      };
      $[2] = t9;
    } else {
      t9 = $[2];
    }
    let t10;
    if ($[3] !== altLabel || $[4] !== displaySrc || $[5] !== isFlagged || $[6] !== sizesAttr) {
      t10 = <OptimizedImage variant="thumbnail" src={displaySrc} alt={altLabel} size="size-full" sizes={sizesAttr} rounded="rounded-card" className="block size-full" imageClassName="size-full object-cover" nsfw={isFlagged} />;
      $[3] = altLabel;
      $[4] = displaySrc;
      $[5] = isFlagged;
      $[6] = sizesAttr;
      $[7] = t10;
    } else {
      t10 = $[7];
    }
    let t11;
    if ($[8] !== pinFailed || $[9] !== pinFailedLabel) {
      t11 = pinFailed ? <div className="absolute top-1 left-1 z-[5] flex size-5 items-center justify-center rounded-full bg-background-secondary/90 text-feedback-warning-content shadow-elevation-card" title={pinFailedLabel}><Icon icon={Alert01IconSolid} className="size-3" /></div> : null;
      $[8] = pinFailed;
      $[9] = pinFailedLabel;
      $[10] = t11;
    } else {
      t11 = $[10];
    }
    let t12;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Icon icon={Cancel01Icon} className="size-3" />;
      $[11] = t12;
    } else {
      t12 = $[11];
    }
    let t13;
    if ($[12] !== onRemove || $[13] !== removeLabel) {
      t13 = <button type="button" onClick={onRemove} aria-label={removeLabel} className="absolute top-1 right-1 z-10 flex size-5 items-center justify-center rounded-full bg-background-secondary/90 text-foreground-primary shadow-elevation-card transition-colors hover:bg-background-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t12}</button>;
      $[12] = onRemove;
      $[13] = removeLabel;
      $[14] = t13;
    } else {
      t13 = $[14];
    }
    let t14;
    if ($[15] !== motionInitial || $[16] !== motionTransition || $[17] !== t10 || $[18] !== t11 || $[19] !== t13 || $[20] !== t8) {
      t14 = <LazyMotion features={domAnimation}><m.div className={t8} initial={motionInitial} animate={t9} transition={motionTransition}>{t10}{t11}{t13}</m.div></LazyMotion>;
      $[15] = motionInitial;
      $[16] = motionTransition;
      $[17] = t10;
      $[18] = t11;
      $[19] = t13;
      $[20] = t8;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    return t14;
  }
  if (isStreaming) {
    const t8 = `shrink-0 rounded-card ${sizeClass}`;
    let t9;
    if ($[22] !== t8) {
      t9 = <Skeleton className={t8} />;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    return t9;
  }
  const t8 = `group relative flex shrink-0 items-center justify-center overflow-hidden rounded-card border-2 border-dashed border-border-primary bg-background-tertiary transition-colors hover:border-border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary ${sizeClass}`;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon icon={Upload01Icon} className="size-5 text-foreground-secondary" />;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== altLabel || $[26] !== onClickOpen || $[27] !== t8) {
    t10 = <button type="button" onClick={onClickOpen} aria-label={altLabel} className={t8}>{t9}</button>;
    $[25] = altLabel;
    $[26] = onClickOpen;
    $[27] = t8;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}